import { FacilityOrder, FacilitySort, Prefecture } from '@gyym/sdk'
import {
  Button,
  ButtonList,
  Empty,
  FacilityCard,
  Link,
  Loading,
  Pagination,
  // TextTabs,
  SelectField,
  SwiperStyles,
  Typography,
} from '@gyym/ui'
import { List, Map, PinDrop, SearchCondition } from '@gyym/ui/icons'
import React from 'react'

import { FacilitySlide } from '@/app/_components/TopView/FacilitySlide'
import {
  defaultSwiperOptions,
  FacilityCardSwiper,
} from '@/components/UI/FacilityCardSwiper'
import { MapView } from '@/components/Views/MapView'
import tokyoConfig from '@/config/tokyo.json'
import { getFacilityAwardYear } from '@/modules/Awards'
import { buildFacilityLink, useAvailableActivities } from '@/modules/Facilities'
import { useNearbyFacilities } from '@/modules/Facilities/hooks/useNearbyFacilities'
import {
  searchQueryConverter,
  useSearchConditionsModal,
  useSearchData,
} from '@/modules/FacilitySearch'
import { useToggleFavorite } from '@/modules/Favorites'
import { usePrefectures } from '@/modules/Prefectures'
import { buildProgramLink } from '@/modules/Programs'

import { useSearch } from '../SearchProvider'
import { useSearchFacilities } from '../useSearchFacilities'
import { Container, FloatingButtonList, MapWrapper } from './styles'

export interface RightContentProps {
  heading: string
  prevPage?: {
    label: string
    href:
      | string
      | {
          pathname: string
          query: Record<string, string | string[]>
        }
    as?: string
  }

  /**
   * Whether the current page is search result page (with multiple conditions)
   */
  isSearchResult?: boolean
}

enum SortOption {
  PRICE = 'price',
  DISTANCE = 'distance',
  REVIEW = 'review',
  NEW = 'new',
  REVIEW_COUNT = 'reviewCount',
}

const sortOptions = [
  {
    label: '価格の安い順',
    value: SortOption.PRICE,
  },
  {
    label: '現在地から近い順',
    value: SortOption.DISTANCE,
  },
  {
    label: '評価の高い順',
    value: SortOption.REVIEW,
  },
  {
    label: '新着順',
    value: SortOption.NEW,
  },
  {
    label: 'レビュー数の多い順',
    value: SortOption.REVIEW_COUNT,
  },
] as const

type SortOptionValue = (typeof sortOptions)[number]['value']
type SortValue = {
  sortBy: FacilitySort
  order: FacilityOrder
}

const sortValues: {
  [key in SortOptionValue]: SortValue
} = {
  distance: {
    sortBy: FacilitySort.DISTANCE,
    order: FacilityOrder.ASC,
  },
  price: {
    sortBy: FacilitySort.MINIMUM_POINT,
    order: FacilityOrder.ASC,
  },
  review: {
    sortBy: FacilitySort.REVIEW_SCORE,
    order: FacilityOrder.DESC,
  },
  new: {
    sortBy: FacilitySort.CREATED_AT,
    order: FacilityOrder.DESC,
  },
  reviewCount: {
    sortBy: FacilitySort.REVIEW_COUNT,
    order: FacilityOrder.DESC,
  },
}

const getSortOptionByValue = (value: SortValue | undefined): SortOption => {
  if (!value) {
    return sortOptions[0].value
  }

  for (const [key, val] of Object.entries(sortValues)) {
    if (val.sortBy === value.sortBy && val.order === value.order) {
      return key as SortOption
    }
  }

  return sortOptions[0].value
}

export const RightContent = ({
  heading,
  prevPage,
  isSearchResult,
}: RightContentProps) => {
  const openSearchConditionsModal = useSearchConditionsModal()

  const {
    isMapView,
    searchParams,
    search,
    isFiltered,
    currentLocation,
    getLocation,
  } = useSearch()

  const { area, prefecture, city, activity, tag } = useSearchData(searchParams)
  const { data: prefectures } = usePrefectures()

  const availableActivities = useAvailableActivities()
  // Exclude current activity
  const activities = availableActivities?.filter((a) => a.id !== activity?.id)

  const { data: nearbyFacilities = [] } = useNearbyFacilities({
    revalidateOnMount: true,
  })

  const isTokyo = prefecture
    ? prefecture.id === Prefecture.TOKYO
    : area
    ? area.prefectureId === Prefecture.TOKYO
    : false

  const isTokyoArea =
    isTokyo ||
    (city
      ? tokyoConfig['23wards'].map((w) => w.code).includes(city.code) ||
        tokyoConfig.others.map((w) => w.code).includes(city.code)
      : false)

  // Exclude current city if not filtered by activity
  const tokyo23wards = !activity
    ? tokyoConfig['23wards'].filter((w) => w.code !== city?.code)
    : tokyoConfig['23wards']

  // Exclude current city if not filtered by activity
  const tokyoOthers = !activity
    ? tokyoConfig.others.filter((w) => w.code !== city?.code)
    : tokyoConfig.others

  // 位置情報の合成・SWR キーは SearchProvider / useSearchFacilities に一元化
  const { data: { data, meta } = {}, isValidating } = useSearchFacilities()

  const {
    onToggle: onToggleFavorite,
    favoritedFacilityIds,
    isFavoriteLoading,
  } = useToggleFavorite()

  const facilities = data || []

  const [totalPages, setTotalPages] = React.useState<number>(
    meta?.lastPage ?? 0,
  )
  React.useEffect(() => {
    // Use previous total page
    setTotalPages((v) => meta?.lastPage ?? v)
  }, [meta?.lastPage])

  // Omit sort/order/pagination params for map search
  const mapSearchParams = React.useMemo(
    () => ({
      ...searchParams,
      sortBy: undefined,
      order: undefined,
      page: undefined,
      numPerPage: undefined,
    }),
    [searchParams],
  )

  const sortValue = getSortOptionByValue(
    searchParams.sortBy
      ? {
          sortBy: searchParams.sortBy,
          order: searchParams.order ?? FacilityOrder.DESC,
        }
      : undefined,
  )

  return (
    <Container isMapView={isMapView}>
      {!isMapView && (
        <>
          <Typography variant="headline2" as="h2">
            {heading}
          </Typography>

          <div className="mt-6 flex flex-col gap-6">
            <div className="flex items-center justify-between">
              {!(sortValue === SortOption.DISTANCE && currentLocation) && (
                <Button
                  variant="primary-text"
                  size="sm"
                  onClick={async () => {
                    const loc = await getLocation()

                    search({
                      sortBy: FacilitySort.DISTANCE,
                      order: FacilityOrder.ASC,
                      latitude: loc.lat,
                      longitude: loc.lng,
                    })
                  }}
                >
                  <PinDrop /> 現在地順
                </Button>
              )}

              <div className="ml-auto flex w-[210px]">
                <SelectField<SortOption>
                  value={sortValue}
                  options={sortOptions}
                  onChange={async (value) => {
                    if (value === SortOption.DISTANCE) {
                      const loc = await getLocation()

                      search({
                        sortBy: FacilitySort.DISTANCE,
                        order: FacilityOrder.ASC,
                        latitude: loc.lat,
                        longitude: loc.lng,
                      })
                    }

                    search({
                      sortBy: sortValues[value].sortBy,
                      order: sortValues[value].order,
                    })
                  }}
                />
              </div>
            </div>

            <div className="flex flex-col gap-4">
              {!data && isValidating ? (
                <Loading />
              ) : facilities.length > 0 ? (
                <>
                  {facilities.map((facility, i) => (
                    <FacilityCard
                      variant="programs"
                      key={facility.id}
                      titleAs="h3"
                      programTitleAs="h4"
                      {...facility}
                      awardYear={getFacilityAwardYear(facility.id)}
                      // Prioritize first 3 facility images (including the first 3 programs shown by default)
                      priority={i < 3}
                      isFavorited={favoritedFacilityIds.includes(facility.id)}
                      onFavoriteClick={onToggleFavorite}
                      isFavoriteLoading={isFavoriteLoading}
                      {...buildFacilityLink(facility)}
                      programLinkBuilder={(program) =>
                        buildProgramLink(facility, program)
                      }
                    />
                  ))}

                  <div className="mt-10 flex justify-center">
                    <Pagination
                      currentPage={searchParams.page ?? 1}
                      totalPages={totalPages ?? 1}
                      onChange={(page) => {
                        search({ page })

                        // Scroll to top
                        requestAnimationFrame(() => {
                          window?.scrollTo({
                            top: 0,
                            behavior: 'smooth',
                          })
                        })
                      }}
                    />
                  </div>
                </>
              ) : (
                <Empty>
                  検索条件にあう施設が見つかりません。
                  <br />
                  条件を変更して絞り込み直してください。
                </Empty>
              )}
            </div>
          </div>
        </>
      )}

      {isMapView && (
        <MapWrapper>
          <MapView searchParams={mapSearchParams} />
        </MapWrapper>
      )}

      {nearbyFacilities.length > 0 && !isMapView && (
        <div className="mt-[60px] flex flex-col gap-4">
          <Typography variant="headline2" as="h2">
            あなたにおすすめの施設
          </Typography>

          <SwiperStyles />

          <FacilityCardSwiper
            id="NearbyFacilities"
            options={{
              ...defaultSwiperOptions,
              breakpoints: {
                0: {
                  slidesPerView: 2,
                },
              },
            }}
            slides={nearbyFacilities.map((facility) => (
              <FacilitySlide
                key={facility.id}
                {...facility}
                awardYear={getFacilityAwardYear(facility.id)}
                {...buildFacilityLink(facility)}
              />
            ))}
          />
        </div>
      )}

      {!isMapView && isTokyoArea && (
        <div className="mt-[60px] flex flex-col gap-4">
          <Typography variant="headline2" as="h2">
            {city
              ? `${city.name}以外のエリア`
              : prefecture
              ? prefecture.name
              : 'その他のエリア'}
            でビジター利用(都度利用)可能な施設を探す
          </Typography>

          <ul className="flex flex-wrap gap-2">
            {tokyo23wards.map(({ name, code }) => {
              const { as, href } = searchQueryConverter.toLink(
                {
                  ...searchParams,
                  page: undefined,
                  prefectureIds: [Prefecture.TOKYO],
                  cityCode: `${code}`,
                },
                prefectures ?? [],
              )

              return (
                <li key={name}>
                  <Button variant="primary-outlined" size="sm" asChild>
                    <Link href={href} as={as}>
                      {name}
                    </Link>
                  </Button>
                </li>
              )
            })}
          </ul>

          <ul className="flex flex-wrap gap-2">
            {tokyoOthers.map(({ name, code }) => {
              const { as, href } = searchQueryConverter.toLink(
                {
                  ...searchParams,
                  page: undefined,
                  prefectureIds: [Prefecture.TOKYO],
                  cityCode: `${code}`,
                },
                prefectures ?? [],
              )

              return (
                <li key={name}>
                  <Button variant="primary-outlined" size="sm" asChild>
                    <Link href={href} as={as}>
                      {name}
                    </Link>
                  </Button>
                </li>
              )
            })}
          </ul>
        </div>
      )}

      {!isSearchResult && !isMapView && activities.length && !tag && (
        <div className="mt-[60px] flex flex-col gap-4">
          <Typography variant="headline2" as="h2">
            {(city ?? area ?? prefecture)?.name
              ? `${(city ?? area ?? prefecture)?.name}の`
              : ''}
            ビジター利用(都度利用)可能な施設をアクティビティで探す
          </Typography>

          <ul className="flex flex-wrap gap-2">
            {activities.map(({ id, name, slug }) => (
              <li key={slug}>
                <Link
                  variant="primary"
                  {...searchQueryConverter.toLink(
                    {
                      ...searchParams,
                      page: undefined,
                      cityCode: city?.code ? `${city.code}` : undefined,
                      areaIds: area?.id ? [area.id] : undefined,
                      prefectureIds: prefecture?.id
                        ? [prefecture.id]
                        : undefined,
                      activityIds: [id],
                    },
                    prefectures ?? [],
                  )}
                >
                  {name}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!isMapView && prevPage && (
        <div className="mt-[60px] flex justify-center">
          <Button className="w-[314px]" variant="primary-outlined" asChild>
            <Link href={prevPage.href} as={prevPage.as}>
              {prevPage.label}のフィットネスジム・スタジオ一覧をみる
            </Link>
          </Button>
        </div>
      )}

      {/* Show the floating button only when in map mode, or list data exists */}
      {isMapView || data ? (
        <FloatingButtonList isMapView={isMapView}>
          <ButtonList>
            <ButtonList.Item
              iconBefore={<SearchCondition indicator={isFiltered} />}
              onClick={() => {
                openSearchConditionsModal({
                  values: searchParams,
                  onSubmit: (values) => {
                    search(
                      {
                        ...values,
                        // Reset cityCode and page on search
                        cityCode: undefined,
                        page: 1,
                      },
                      {
                        isMapView,
                      },
                    )
                  },
                })
              }}
              tabletOnly
            >
              条件を変更
            </ButtonList.Item>

            {!isMapView && (
              <ButtonList.Item
                iconBefore={<Map />}
                onClick={() => {
                  search({}, { isMapView: true })
                }}
              >
                マップ表示
              </ButtonList.Item>
            )}

            {isMapView && (
              <ButtonList.Item
                iconBefore={<List />}
                onClick={() => {
                  search(
                    {
                      sortBy: FacilitySort.DISTANCE,
                      order: FacilityOrder.ASC,
                      latitude: currentLocation.lat,
                      longitude: currentLocation.lng,
                    },
                    { isMapView: false },
                  )
                }}
              >
                リスト表示
              </ButtonList.Item>
            )}
          </ButtonList>
        </FloatingButtonList>
      ) : null}
    </Container>
  )
}
